import { useApiQuery } from './useApiQuery';
import { useApiMutation } from './useApiMutation';
import { queryKeys } from './queryKeys';
import { ledgerQueryKeys } from './useGeneralLedger';
import { useQueryClient, type QueryKey } from '@tanstack/react-query';
import { DrawdownService } from '@/services/drawdowns/drawdownService';
import { DrawdownMatrixService } from '@/services/drawdowns/drawdownMatrixService';
import { APIError } from '@/utils/api-client';
import type {
  DrawdownSummary,
  DrawdownStatusUpdate,
  DrawdownFilters,
  DrawdownListResponse,
  DrawdownDetailResponse,
  Drawdown,
  OverduePaymentStatus,
  RecordFullPaymentRequest,
} from '@/types/drawdown';

// Query hooks
export const useDrawdowns = (params?: {
  skip?: number;
  limit?: number;
  fund_id?: string;
  status?: string;
  include_investors?: boolean;
}) => {
  return useApiQuery(
    [...queryKeys.drawdowns.all, 'list', params],
    () => DrawdownService.getDrawdowns(params),
    {
      staleTime: 1 * 60 * 1000, // 1 minute - drawdowns change frequently
    }
  );
};

export const useDrawdown = (
  drawdownId: string,
  options?: {
    includeInvestors?: boolean;
    includePayments?: boolean;
    fundId?: string;
  }
) => {
  const {
    includeInvestors = true,
    includePayments = true,
    fundId,
  } = options || {};

  return useApiQuery(
    [
      ...queryKeys.drawdowns.detail(drawdownId),
      { includeInvestors, includePayments, fundId },
    ],
    () =>
      DrawdownService.getDrawdown(
        drawdownId,
        includeInvestors,
        includePayments,
        fundId
      ),
    {
      enabled: !!drawdownId,
      staleTime: 1 * 60 * 1000, // 1 minute
    }
  );
};

export const useDrawdownMatrix = (fundId: string) => {
  return useApiQuery(
    queryKeys.drawdowns.matrix(fundId),
    () => DrawdownMatrixService.getMatrix(fundId),
    {
      enabled: !!fundId,
      staleTime: 1 * 60 * 1000,
    }
  );
};

const usePaymentStatistics = (params?: {
  fund_id?: string;
  date_from?: string;
  date_to?: string;
}) => {
  return useApiQuery(
    [...queryKeys.drawdowns.all, 'payment-statistics', params],
    () => DrawdownService.getPaymentStatistics(params),
    {
      staleTime: 2 * 60 * 1000, // 2 minutes
    }
  );
};

export const useOverduePayments = (params: {
  page: number;
  page_size: number;
  fund_id?: string;
  search?: string;
  status?: OverduePaymentStatus;
}) => {
  return useApiQuery(
    [...queryKeys.drawdowns.all, 'overdue-payments', params],
    () => DrawdownService.getOverduePayments(params),
    {
      staleTime: 1 * 60 * 1000, // 1 minute - important to keep fresh
    }
  );
};

// Mutation hooks
export const useUpdateDrawdownStatus = () => {
  const queryClient = useQueryClient();

  return useApiMutation(
    ({
      drawdownId,
      status,
      fundId,
    }: {
      drawdownId: string;
      status: DrawdownStatusUpdate;
      fundId?: string;
    }) => DrawdownService.updateDrawdownStatus(drawdownId, status, fundId),
    {
      successMessage: 'Status updated successfully',
      // Optimistic update for status changes
      onMutate: async ({ drawdownId, status }) => {
        // Cancel any outgoing refetches for all drawdown queries
        await queryClient.cancelQueries({ queryKey: queryKeys.drawdowns.all });

        // Snapshot all affected queries
        const previousQueries = new Map();
        queryClient
          .getQueriesData({ queryKey: queryKeys.drawdowns.all })
          .forEach(([key, data]) => {
            previousQueries.set(JSON.stringify(key), { key, data });
          });

        // Optimistically update all matching queries
        queryClient.setQueriesData(
          { queryKey: queryKeys.drawdowns.all },
          (
            old:
              | DrawdownListResponse
              | DrawdownDetailResponse
              | Drawdown
              | undefined
          ) => {
            if (!old) return old;

            // Update in list view (e.g., { drawdowns: [...], total_count: N })
            if ('drawdowns' in old && Array.isArray(old.drawdowns)) {
              return {
                ...old,
                drawdowns: old.drawdowns.map((d: DrawdownSummary) =>
                  d.id === drawdownId ? { ...d, status: status.status } : d
                ),
              };
            }

            // Update in detail view - nested structure (e.g., { drawdown: {...} })
            if ('drawdown' in old && old.drawdown.id === drawdownId) {
              return {
                ...old,
                drawdown: {
                  ...old.drawdown,
                  status: status.status,
                },
              };
            }

            // Update in detail view - flat structure (e.g., { id, status, ... })
            if ('id' in old && old.id === drawdownId && 'status' in old) {
              return { ...old, status: status.status };
            }

            return old;
          }
        );

        return { previousQueries };
      },
      onSuccess: (data, variables) => {
        // Update cache with actual server response
        // This prevents race conditions by not relying on refetch
        const drawdownId = variables.drawdownId;

        // Update all queries with the confirmed server response
        queryClient.setQueriesData(
          { queryKey: queryKeys.drawdowns.all },
          (
            old:
              | DrawdownListResponse
              | DrawdownDetailResponse
              | Drawdown
              | undefined
          ) => {
            if (!old) return old;

            // Update in list view
            if ('drawdowns' in old && Array.isArray(old.drawdowns)) {
              return {
                ...old,
                drawdowns: old.drawdowns.map((d: DrawdownSummary) =>
                  d.id === drawdownId
                    ? { ...d, status: data.status, updated_at: data.updated_at }
                    : d
                ),
              };
            }

            // Update in detail view - nested structure
            if ('drawdown' in old && old.drawdown.id === drawdownId) {
              return {
                ...old,
                drawdown: {
                  ...old.drawdown,
                  status: data.status,
                  updated_at: data.updated_at,
                },
              };
            }

            // Update in detail view - flat structure
            if ('id' in old && old.id === drawdownId && 'status' in old) {
              return {
                ...old,
                status: data.status,
                updated_at: data.updated_at,
              };
            }

            return old;
          }
        );

        // Mark queries as stale without triggering immediate refetch
        // This ensures next mount will refetch, but doesn't cause race condition now
        queryClient.invalidateQueries({
          queryKey: queryKeys.drawdowns.all,
          refetchType: 'none', // Don't refetch immediately, just mark as stale
        });
        // Issuing changes batch statuses (DRAFT -> ISSUED) and queues document
        // generation server-side; refetch every batch-related query for this
        // drawdown (batch list, PDF progress feed, batch preview) so badges
        // and "Generating documents" indicators update immediately instead of
        // waiting for a remount/refocus
        if (data.status === 'issued') {
          queryClient.invalidateQueries({
            queryKey: [...queryKeys.drawdowns.detail(drawdownId), 'batches'],
          });
        }
        // Issuing drawdowns creates general ledger entries, so invalidate ledger queries
        queryClient.invalidateQueries({
          queryKey: ledgerQueryKeys.all,
          refetchType: 'none',
        });
      },
      onError: (_err, _variables, context) => {
        // Revert all optimistic updates
        if (context?.previousQueries) {
          context.previousQueries.forEach(
            (value: { key: QueryKey; data: unknown }) => {
              queryClient.setQueryData(value.key, value.data);
            }
          );
        }
      },
    }
  );
};

export const useDeleteDrawdown = () => {
  return useApiMutation(
    ({ drawdownId, fundId }: { drawdownId: string; fundId?: string }) =>
      DrawdownService.deleteDrawdown(drawdownId, fundId),
    {
      invalidateQueries: [queryKeys.drawdowns.all, queryKeys.funds.all],
      successMessage: 'Drawdown deleted successfully',
    }
  );
};

// Record full payment for investor (admin action)
export const useRecordFullPayment = () => {
  return useApiMutation(
    ({
      fundId,
      drawdownId,
      drawdownInvestorId,
      data,
    }: {
      fundId: string;
      drawdownId: string;
      drawdownInvestorId: string;
      data: RecordFullPaymentRequest;
    }) =>
      DrawdownService.recordInvestorFullPayment(
        fundId,
        drawdownId,
        drawdownInvestorId,
        data
      ),
    {
      invalidateQueries: [
        queryKeys.drawdowns.all,
        [...queryKeys.drawdowns.all, 'payments'],
        [...queryKeys.drawdowns.all, 'payment-statistics'],
        [...queryKeys.drawdowns.all, 'overdue-payments'],
        ledgerQueryKeys.all,
      ],
      successMessage: 'Payment recorded successfully',
    }
  );
};

// Unlink a payment from the investor it was credited to
export const useRemoveDrawdownPayment = () => {
  return useApiMutation(
    ({
      fundId,
      drawdownId,
      paymentId,
    }: {
      fundId: string;
      drawdownId: string;
      paymentId: string;
    }) => DrawdownService.removePayment(fundId, drawdownId, paymentId),
    {
      invalidateQueries: [
        queryKeys.drawdowns.all,
        queryKeys.fundTransactions.all,
        [...queryKeys.drawdowns.all, 'payments'],
        [...queryKeys.drawdowns.all, 'payment-statistics'],
        [...queryKeys.drawdowns.all, 'overdue-payments'],
        ledgerQueryKeys.all,
      ],
      successMessage: 'Payment removed',
    }
  );
};

// PDF download
export const useDownloadDrawdownPDF = () => {
  return useApiMutation<Blob, APIError, { drawdownId: string }>(
    ({ drawdownId }) => DrawdownService.downloadDrawdownPDF(drawdownId),
    {
      showSuccessToast: false, // Handle download manually
      showErrorToast: true,
    }
  );
};

// Regenerate all PDFs for all batches in a drawdown
export const useRegenerateAllDrawdownPDFs = () => {
  const queryClient = useQueryClient();

  return useApiMutation(
    ({ drawdownId, fundId }: { drawdownId: string; fundId?: string }) =>
      DrawdownService.regenerateAllDrawdownPDFs(drawdownId, fundId),
    {
      showSuccessToast: true,
      successMessage: 'PDF regeneration started for all batches',
      showErrorToast: true,
      onSuccess: () => {
        // Invalidate relevant queries to refresh the UI
        queryClient.invalidateQueries({ queryKey: queryKeys.drawdowns.all });
      },
    }
  );
};

// Send email notifications to all investors in a drawdown
export const useSendDrawdownEmails = () => {
  const queryClient = useQueryClient();

  return useApiMutation<
    {
      message: string;
      drawdown_id: string;
      emails_queued: number;
      new_only: boolean;
    },
    Error,
    { fundId: string; drawdownId: string; newOnly: boolean }
  >(
    ({ fundId, drawdownId, newOnly }) =>
      DrawdownService.sendDrawdownEmails(fundId, drawdownId, newOnly),
    {
      showSuccessToast: true,
      successMessage: (data) =>
        `${data.emails_queued} email${data.emails_queued !== 1 ? 's' : ''} queued successfully`,
      showErrorToast: true,
      onSuccess: (_, variables) => {
        // Invalidate drawdown queries to refresh email status
        queryClient.invalidateQueries({
          queryKey: queryKeys.drawdowns.detail(variables.drawdownId),
        });
        queryClient.invalidateQueries({
          queryKey: queryKeys.drawdowns.all,
        });
      },
    }
  );
};

// Download all PDFs from all batches in a drawdown as a ZIP
export const useDownloadAllDrawdownPDFs = () =>
  useApiMutation(
    ({ fundId, drawdownId }: { fundId: string; drawdownId: string }) =>
      DrawdownService.downloadAllDrawdownPDFsZip(fundId, drawdownId),
    {
      showSuccessToast: false, // Handle download manually
      showErrorToast: true,
    }
  );

// Utility hook for combined drawdown data
export const useDrawdownDashboard = (
  fundId?: string,
  filters?: DrawdownFilters
) => {
  const drawdownsQuery = useDrawdowns({
    fund_id: fundId || filters?.fund_id,
    status: filters?.status,
    include_investors: true,
  });

  const statsQuery = usePaymentStatistics({
    fund_id: fundId || filters?.fund_id,
    date_from: filters?.date_from,
    date_to: filters?.date_to,
  });

  // Overdue payments are paginated and fetched by OverduePaymentsTab itself.
  return {
    drawdowns: drawdownsQuery.data?.drawdowns || [],
    totalCount: drawdownsQuery.data?.total_count || 0,
    paymentStats: statsQuery.data || null,
    isLoading: drawdownsQuery.isLoading || statsQuery.isLoading,
    isError: drawdownsQuery.isError || statsQuery.isError,
    error: drawdownsQuery.error || statsQuery.error,
    refetchAll: () => {
      drawdownsQuery.refetch();
      statsQuery.refetch();
    },
  };
};

export const useUpdateNoticeAmount = () =>
  useApiMutation(
    ({
      fundId,
      drawdownId,
      drawdownInvestorId,
      drawdownAmount,
    }: {
      fundId: string;
      drawdownId: string;
      drawdownInvestorId: string;
      drawdownAmount: number;
    }) =>
      DrawdownService.updateNoticeAmount(
        fundId,
        drawdownId,
        drawdownInvestorId,
        drawdownAmount
      ),
    {
      invalidateQueries: [queryKeys.drawdowns.all, ledgerQueryKeys.all],
      successMessage: 'Amount updated',
    }
  );
